import { DEBOUNCE_TIME } from 'common/utilities/timeout/timeout';
import debounce from 'debounce';
import type {
  MutableRefObject} from 'react';
import { useEffect, useRef, useState,
} from 'react';

const useRect = <T extends HTMLElement>(defaultRef?: T): [
  DOMRect | undefined,
  MutableRefObject<T | null>,
  (newDefaultRef: T) => void,
] => {
  const ref = useRef<T | null>(defaultRef || null);
  const [rect, setRect] = useState<DOMRect>();

  const set = (e?: Event) => {
    // Ensure we don't listen to the autocomplete list scroll
    if (!(e && e.target && (e.target as HTMLElement).classList?.contains('MuiAutocomplete-listbox'))) {
      setRect(ref.current?.getBoundingClientRect());
    }
  };

  const debouncedSet = debounce((e?: Event) => set(e), DEBOUNCE_TIME.scroll);

  const setRef = (newDefaultRef?: T) => {
    if (newDefaultRef) {
      ref.current = newDefaultRef;
      set();
    }
  };

  const useEffectInEvent = (
    event: 'resize' | 'scroll',
    useCapture?: boolean,
  ) => {
    useEffect(() => {
      set();
      window.addEventListener(event, debouncedSet, useCapture);
      return () => window.removeEventListener(event, debouncedSet, useCapture);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  };

  useEffectInEvent('resize');
  useEffectInEvent('scroll', true);

  return [rect, ref, setRef];
};

export default useRect;
